import { useState, useEffect } from 'react';
import { Building2, Package, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import Layout from '../components/shared/Layout';
import MetricCard from '../components/dashboard/MetricCard';
import ComplianceGauge from '../components/dashboard/ComplianceGauge';
import ActivityChart from '../components/dashboard/ActivityChart';
import ComplianceBreakdown from '../components/dashboard/ComplianceBreakdown';
import RiskHeatMap from '../components/dashboard/RiskHeatMap';
import RadialRiskChart from '../components/dashboard/RadialRiskChart';
import { useAuth } from '../hooks/useAuth';
import { apiGet } from '../config/api';

export default function DashboardPage() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState({
        facilities: 0,
        equipment: 0,
        overdueInspections: 0,
        compliantEquipment: 0,
        overallCompliance: 0,
        labourCodeCompliance: 0,
        apsadCompliance: 0,
    });

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const res = await apiGet('/api/dashboard');
                const data = await res.json();
                if (data.success && data.stats) {
                    setMetrics(data.stats);
                }
            } catch (error) {
                console.error('Failed to fetch dashboard metrics:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    // Upcoming tasks from real inspections
    const [upcomingTasks, setUpcomingTasks] = useState<{
        id: string;
        title: string;
        equipment: string;
        dueDate: string;
        priority: 'high' | 'medium' | 'low';
    }[]>([]);

    useEffect(() => {
        const fetchUpcomingTasks = async () => {
            try {
                const res = await apiGet('/api/inspections');
                const data = await res.json();
                if (data.success && data.inspections) {
                    const now = new Date();
                    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

                    const upcoming = data.inspections
                        .filter((insp: any) => {
                            const dueDate = new Date(insp.nextDueDate);
                            return dueDate >= now && dueDate <= thirtyDaysFromNow;
                        })
                        .sort((a: any, b: any) => new Date(a.nextDueDate).getTime() - new Date(b.nextDueDate).getTime())
                        .slice(0, 5) // Top 5
                        .map((insp: any) => {
                            const dueDate = new Date(insp.nextDueDate);
                            const daysUntil = Math.ceil((dueDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));

                            let priority: 'high' | 'medium' | 'low' = 'low';
                            if (daysUntil <= 3) priority = 'high';
                            else if (daysUntil <= 7) priority = 'medium';

                            const dueDateStr = daysUntil === 0 ? 'Today' :
                                daysUntil === 1 ? '1 day' :
                                    daysUntil <= 7 ? `${daysUntil} days` :
                                        `${Math.ceil(daysUntil / 7)} weeks`;

                            return {
                                id: insp.id,
                                title: `${insp.inspectionType} Inspection`,
                                equipment: `${insp.equipmentType || 'Equipment'} - ${insp.equipmentNumber}`,
                                dueDate: dueDateStr,
                                priority
                            };
                        });

                    setUpcomingTasks(upcoming);
                }
            } catch (error) {
                console.error('Failed to fetch upcoming tasks:', error);
            }
        };

        fetchUpcomingTasks();
    }, []);

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'high':
                return 'border-l-red-500 bg-red-500/10';
            case 'medium':
                return 'border-l-yellow-500 bg-yellow-500/10';
            default:
                return 'border-l-gray-500 bg-gray-500/10';
        }
    };

    return (
        <Layout>
            <div className="min-h-screen p-8">
                {/* Header */}
                <div className="mb-8 animate-fadeIn">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="tech-label">
                            <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
                            SYSTEM ACTIVE
                        </div>
                    </div>
                    <h1 className="text-4xl font-bold text-white mb-2">
                        Welcome back, {user?.fullName?.split(' ')[0] || 'User'}
                    </h1>
                    <p className="text-gray-400">
                        Here's your compliance overview for today
                    </p>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <MetricCard
                        title="Total Facilities"
                        value={metrics.facilities}
                        subtitle="Across all locations"
                        icon={Building2}
                        color="ember"
                        trend={{ value: 12, direction: 'up' }}
                        delay={0}
                    />
                    <MetricCard
                        title="Equipment Inventory"
                        value={metrics.equipment}
                        subtitle={`${metrics.compliantEquipment} compliant`}
                        icon={Package}
                        color="steel"
                        trend={{ value: 8, direction: 'up' }}
                        delay={100}
                    />
                    <MetricCard
                        title="Compliant Equipment"
                        value={metrics.compliantEquipment}
                        subtitle={`${((metrics.compliantEquipment / metrics.equipment) * 100).toFixed(0)}% of total`}
                        icon={CheckCircle2}
                        color="safety"
                        trend={{ value: 5, direction: 'up' }}
                        delay={200}
                    />
                    <MetricCard
                        title="Overdue Inspections"
                        value={metrics.overdueInspections}
                        subtitle="Require immediate attention"
                        icon={AlertCircle}
                        color="alert"
                        trend={{ value: 15, direction: 'down' }}
                        delay={300}
                    />
                </div>

                {/* Compliance Gauges */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    <ComplianceGauge
                        percentage={metrics.overallCompliance}
                        title="Overall Compliance"
                        subtitle="All facilities combined"
                    />
                    <ComplianceGauge
                        percentage={metrics.labourCodeCompliance}
                        title="Labour Code R4227-29"
                        subtitle="Water spray requirements"
                    />
                    <ComplianceGauge
                        percentage={metrics.apsadCompliance}
                        title="APSAD R4 Standard"
                        subtitle="General protection coverage"
                    />
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    <ActivityChart
                        data={[
                            { label: 'Mon', value: 12 },
                            { label: 'Tue', value: 19 },
                            { label: 'Wed', value: 8 },
                            { label: 'Thu', value: 25 },
                            { label: 'Fri', value: 18 },
                            { label: 'Sat', value: 5 },
                            { label: 'Sun', value: 3 },
                        ]}
                        title="Audit Activity"
                        color="ember"
                    />
                    <ComplianceBreakdown
                        data={[
                            { label: 'Fire Detection', value: 45, color: '#10b981' },
                            { label: 'Suppression', value: 28, color: '#3b82f6' },
                            { label: 'Emergency Exits', value: 18, color: '#f59e0b' },
                            { label: 'Training', value: 9, color: '#8b5cf6' },
                        ]}
                        title="Equipment Distribution"
                        centerLabel="Total"
                    />
                </div>

                {/* Risk Heat Map */}
                <div className="mb-8">
                    <RiskHeatMap
                        zones={[
                            { id: '1', name: 'Zone A - North Wing', riskLevel: 'low', score: 92, lastAudit: 'Jan 5, 2026' },
                            { id: '2', name: 'Zone B - East Wing', riskLevel: 'low', score: 88, lastAudit: 'Jan 3, 2026' },
                            { id: '3', name: 'Zone C - South Wing', riskLevel: 'medium', score: 72, lastAudit: 'Dec 28, 2025' },
                            { id: '4', name: 'Zone D - West Wing', riskLevel: 'low', score: 95, lastAudit: 'Jan 7, 2026' },
                            { id: '5', name: 'Zone E - Basement', riskLevel: 'high', score: 58, lastAudit: 'Dec 15, 2025' },
                            { id: '6', name: 'Zone F - Roof Access', riskLevel: 'medium', score: 68, lastAudit: 'Dec 20, 2025' },
                            { id: '7', name: 'Zone G - Parking', riskLevel: 'low', score: 85, lastAudit: 'Jan 2, 2026' },
                            { id: '8', name: 'Zone H - Lobby', riskLevel: 'low', score: 91, lastAudit: 'Jan 6, 2026' },
                        ]}
                    />
                </div>

                {/* D3.js Radial Risk Chart */}
                <div className="mb-8">
                    <RadialRiskChart
                        data={{
                            name: 'Facility',
                            value: 0,
                            riskLevel: 'low',
                            children: [
                                {
                                    name: 'Fire Detection',
                                    value: 0,
                                    riskLevel: 'low',
                                    children: [
                                        { name: 'Smoke Sensors', value: 45, riskLevel: 'low' },
                                        { name: 'Heat Detectors', value: 32, riskLevel: 'low' },
                                        { name: 'Flame Scanners', value: 18, riskLevel: 'medium' },
                                    ],
                                },
                                {
                                    name: 'Suppression',
                                    value: 0,
                                    riskLevel: 'medium',
                                    children: [
                                        { name: 'Sprinklers', value: 28, riskLevel: 'low' },
                                        { name: 'Gas Systems', value: 15, riskLevel: 'high' },
                                        { name: 'Foam Units', value: 12, riskLevel: 'medium' },
                                    ],
                                },
                                {
                                    name: 'Egress',
                                    value: 0,
                                    riskLevel: 'high',
                                    children: [
                                        { name: 'Emergency Exits', value: 22, riskLevel: 'critical' },
                                        { name: 'Stairwells', value: 18, riskLevel: 'high' },
                                        { name: 'Signage', value: 8, riskLevel: 'low' },
                                    ],
                                },
                                {
                                    name: 'Training',
                                    value: 0,
                                    riskLevel: 'medium',
                                    children: [
                                        { name: 'Staff Drills', value: 35, riskLevel: 'low' },
                                        { name: 'Certifications', value: 25, riskLevel: 'medium' },
                                        { name: 'Equipment Use', value: 20, riskLevel: 'medium' },
                                    ],
                                },
                            ],
                        }}
                        title="Hierarchical Risk Analysis"
                        width={400}
                        height={400}
                    />
                </div>

                {/* Upcoming Tasks */}
                <div className="card-glass p-6 animate-slideUp" style={{ animationDelay: '400ms' }}>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <Clock className="w-6 h-6 text-ember-500" />
                            <h2 className="text-xl font-display font-bold text-charcoal-950">
                                Upcoming Tasks
                            </h2>
                        </div>
                        <button className="text-sm text-ember-600 hover:text-ember-700 font-medium transition-colors">
                            View All →
                        </button>
                    </div>

                    <div className="space-y-3">
                        {upcomingTasks.map((task) => (
                            <div
                                key={task.id}
                                className={`p-4 border-l-4 rounded-r-xl ${getPriorityColor(task.priority)} transition-all duration-200 hover:shadow-md cursor-pointer`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-charcoal-900 mb-1">
                                            {task.title}
                                        </h4>
                                        <p className="text-sm text-steel-600">{task.equipment}</p>
                                    </div>
                                    <div className="text-right">
                                        <span className="inline-block px-3 py-1 bg-white rounded-lg text-sm font-medium text-steel-700 shadow-sm">
                                            Due in {task.dueDate}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {upcomingTasks.length === 0 && (
                        <div className="text-center py-12">
                            <CheckCircle2 className="w-12 h-12 text-safety-500 mx-auto mb-3" />
                            <p className="text-steel-600">All caught up! No pending tasks.</p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
